import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Mail, AlertCircle, CheckCircle, Bell, ShieldAlert } from 'lucide-react';
import SearchableSelect from './SearchableSelect';

const EmailConfiguration = () => {
  const [config, setConfig] = useState({
    smtpHost: '',
    smtpPort: 587,
    smtpSecure: false,
    smtpUser: '',
    smtpPassword: '',
    fromEmail: '',
    fromName: 'Receipt Manager',
    taggunApiKey: '',
    isConfigured: false
  });

  const [reminderConfig, setReminderConfig] = useState({
    enabled: false,
    frequency: 'weekly',
    timeOfDay: '09:00',
    dayOfWeek: 1
  });

  const [violationNotifConfig, setViolationNotifConfig] = useState({
    notifyOnNewViolations: true,
    notifyHighSeverityOnly: false
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingReminder, setSavingReminder] = useState(false);
  const [savingViolationNotif, setSavingViolationNotif] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testingReminder, setTestingReminder] = useState(false);
  const [testingViolationNotif, setTestingViolationNotif] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [testEmail, setTestEmail] = useState('');

  useEffect(() => {
    fetchConfig();
    fetchReminderConfig();
    fetchViolationNotifConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/email-config');
      setConfig(response.data);
      setError('');
    } catch (error) {
      console.error('Error fetching email configuration:', error);
      setError('Failed to fetch email configuration');
    } finally {
      setLoading(false);
    }
  };

  const fetchReminderConfig = async () => {
    try {
      const response = await axios.get('/api/reminder-config');
      setReminderConfig(response.data);
    } catch (error) {
      console.error('Error fetching reminder configuration:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const response = await axios.put('/api/email-config', config);
      setConfig(response.data);
      setSuccess('Email configuration saved successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error saving email configuration:', error);
      setError(error.response?.data?.error || 'Failed to save email configuration');
      setTimeout(() => setError(''), 5000);
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    if (!testEmail) {
      setError('Please enter a test email address');
      setTimeout(() => setError(''), 3000);
      return;
    }

    setTesting(true);
    setError('');
    setSuccess('');

    try {
      await axios.post('/api/email-config/test', { testEmail });
      setSuccess(`Test email sent successfully to ${testEmail}`);
      setTimeout(() => setSuccess(''), 5000);
    } catch (error) {
      console.error('Error sending test email:', error);
      setError(error.response?.data?.error || error.response?.data?.details || 'Failed to send test email');
      setTimeout(() => setError(''), 5000);
    } finally {
      setTesting(false);
    }
  };

  const handleReminderSubmit = async (e) => {
    e.preventDefault();
    setSavingReminder(true);
    setError('');
    setSuccess('');

    try {
      const response = await axios.put('/api/reminder-config', reminderConfig);
      setReminderConfig(response.data);
      setSuccess('Reminder settings saved successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error saving reminder configuration:', error);
      setError(error.response?.data?.error || 'Failed to save reminder settings');
      setTimeout(() => setError(''), 5000);
    } finally {
      setSavingReminder(false);
    }
  };

  const handleTestReminder = async () => {
    setTestingReminder(true);
    setError('');
    setSuccess('');

    try {
      await axios.post('/api/reminder-config/test');
      setSuccess('Test reminders sent to all users with unmatched transactions');
      setTimeout(() => setSuccess(''), 5000);
    } catch (error) {
      console.error('Error sending test reminders:', error);
      setError(error.response?.data?.error || error.response?.data?.details || 'Failed to send test reminders');
      setTimeout(() => setError(''), 5000);
    } finally {
      setTestingReminder(false);
    }
  };

  const fetchViolationNotifConfig = async () => {
    try {
      const response = await axios.get('/api/time-audit-config/notification-settings');
      setViolationNotifConfig(response.data);
    } catch (error) {
      console.error('Error fetching violation notification config:', error);
    }
  };

  const handleViolationNotifSubmit = async (e) => {
    e.preventDefault();
    setSavingViolationNotif(true);
    setError('');
    setSuccess('');

    try {
      const response = await axios.put('/api/time-audit-config/notification-settings', violationNotifConfig);
      setViolationNotifConfig(response.data);
      setSuccess('Time violation notification settings saved successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error saving violation notification config:', error);
      setError(error.response?.data?.error || 'Failed to save violation notification settings');
      setTimeout(() => setError(''), 5000);
    } finally {
      setSavingViolationNotif(false);
    }
  };

  const handleTestViolationNotif = async () => {
    setTestingViolationNotif(true);
    setError('');
    setSuccess('');

    try {
      await axios.post('/api/time-audit-config/test-notification');
      setSuccess('Test violation notification sent to all managers with pending violations');
      setTimeout(() => setSuccess(''), 5000);
    } catch (error) {
      console.error('Error sending test violation notification:', error);
      setError(error.response?.data?.error || error.response?.data?.details || 'Failed to send test notification');
      setTimeout(() => setError(''), 5000);
    } finally {
      setTestingViolationNotif(false);
    }
  };

  const getDayName = (dayNum) => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[dayNum];
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold flex items-center gap-2">
          <Mail className="h-6 w-6" />
          Application Configuration
        </h2>
        <p className="text-gray-600 mt-2">
          Configure SMTP settings for emails, API keys, and other application settings
        </p>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 flex items-center gap-2">
          <AlertCircle className="h-5 w-5" />
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4 flex items-center gap-2">
          <CheckCircle className="h-5 w-5" />
          {success}
        </div>
      )}

      {config.isConfigured && (
        <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded mb-4 flex items-center gap-2">
          <CheckCircle className="h-5 w-5" />
          Email is configured and ready to use
        </div>
      )}

      <div className="bg-white shadow-md rounded-lg p-6">
        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            {/* Email Configuration Section */}
            <div className="mb-4">
              <h3 className="text-lg font-semibold mb-4">Email Configuration</h3>
            </div>

            {/* SMTP Host */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                SMTP Host *
              </label>
              <input
                type="text"
                value={config.smtpHost || ''}
                onChange={(e) => setConfig({ ...config, smtpHost: e.target.value })}
                placeholder="smtp.gmail.com"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Your SMTP server address (e.g., smtp.gmail.com, smtp.office365.com)
              </p>
            </div>

            {/* SMTP Port and Secure */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  SMTP Port *
                </label>
                <input
                  type="number"
                  value={config.smtpPort || 587}
                  onChange={(e) => setConfig({ ...config, smtpPort: parseInt(e.target.value) })}
                  min="1"
                  max="65535"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Common ports: 587 (TLS), 465 (SSL), 25 (non-encrypted)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Connection Security
                </label>
                <SearchableSelect
                  value={config.smtpSecure ? 'true' : 'false'}
                  onChange={(value) => setConfig({ ...config, smtpSecure: value === 'true' })}
                  options={[
                    { id: 'false', label: 'TLS (Port 587)' },
                    { id: 'true', label: 'SSL (Port 465)' }
                  ]}
                  getOptionLabel={(opt) => opt.label}
                  getOptionValue={(opt) => opt.id}
                  placeholder="Select connection security"
                  searchPlaceholder="Search..."
                  allowEmpty={false}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Choose TLS for port 587, SSL for port 465
                </p>
              </div>
            </div>

            {/* SMTP User */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                SMTP Username *
              </label>
              <input
                type="text"
                value={config.smtpUser || ''}
                onChange={(e) => setConfig({ ...config, smtpUser: e.target.value })}
                placeholder="your-email@gmail.com"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Usually your email address
              </p>
            </div>

            {/* SMTP Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                SMTP Password *
              </label>
              <input
                type="password"
                value={config.smtpPassword || ''}
                onChange={(e) => setConfig({ ...config, smtpPassword: e.target.value })}
                placeholder="••••••••"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required={!config.isConfigured}
              />
              <p className="text-xs text-gray-500 mt-1">
                For Gmail, use an App Password (not your regular password)
              </p>
            </div>

            {/* From Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                From Email Address *
              </label>
              <input
                type="email"
                value={config.fromEmail || ''}
                onChange={(e) => setConfig({ ...config, fromEmail: e.target.value })}
                placeholder="noreply@yourcompany.com"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Email address that will appear in the "From" field
              </p>
            </div>

            {/* From Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                From Name
              </label>
              <input
                type="text"
                value={config.fromName || 'Receipt Manager'}
                onChange={(e) => setConfig({ ...config, fromName: e.target.value })}
                placeholder="Receipt Manager"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Name that will appear in the "From" field
              </p>
            </div>

            {/* Divider */}
            <div className="pt-6 border-t border-gray-200">
              <h3 className="text-lg font-semibold mb-4">API Configuration</h3>
            </div>

            {/* Taggun API Key */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Taggun API Key
              </label>
              <input
                type="password"
                value={config.taggunApiKey || ''}
                onChange={(e) => setConfig({ ...config, taggunApiKey: e.target.value })}
                placeholder="••••••••"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                API key for Taggun receipt OCR service. Get your key at{' '}
                <a
                  href="https://www.taggun.io"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 underline"
                >
                  taggun.io
                </a>
              </p>
            </div>

            {/* Save Button */}
            <div className="pt-4">
              <button
                type="submit"
                disabled={saving}
                className="px-6 py-2 bg-blue-500 hover:bg-blue-600 text-white font-medium rounded-lg transition-colors duration-200 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {saving ? 'Saving...' : 'Save Configuration'}
              </button>
            </div>
          </div>
        </form>

        {/* Test Email Section */}
        {config.isConfigured && (
          <div className="mt-8 pt-8 border-t border-gray-200">
            <h3 className="text-lg font-semibold mb-4">Test Email Configuration</h3>
            <p className="text-sm text-gray-600 mb-4">
              Send a test email to verify your SMTP settings are working correctly
            </p>
            <div className="flex gap-4">
              <input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="test@example.com"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                type="button"
                onClick={handleTestEmail}
                disabled={testing}
                className="px-6 py-2 bg-green-500 hover:bg-green-600 text-white font-medium rounded-lg transition-colors duration-200 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {testing ? 'Sending...' : 'Send Test Email'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Reminder Configuration Section */}
      <div className="bg-white shadow-md rounded-lg p-6 mt-6">
        <div className="mb-4">
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Unmatched Transaction Reminders
          </h2>
          <p className="text-gray-600 mt-2 text-sm">
            Configure automated email reminders for users with unmatched transactions
          </p>
        </div>

        {!config.isConfigured && (
          <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-3 rounded mb-4 flex items-center gap-2">
            <AlertCircle className="h-5 w-5" />
            Email must be configured before enabling reminders
          </div>
        )}

        <form onSubmit={handleReminderSubmit}>
          <div className="space-y-4">
            {/* Enable/Disable Toggle */}
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="reminderEnabled"
                checked={reminderConfig.enabled}
                onChange={(e) => setReminderConfig({ ...reminderConfig, enabled: e.target.checked })}
                disabled={!config.isConfigured}
                className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
              />
              <label htmlFor="reminderEnabled" className="text-sm font-medium text-gray-700">
                Enable automated reminders
              </label>
            </div>

            {reminderConfig.enabled && (
              <>
                {/* Frequency */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Reminder Frequency *
                  </label>
                  <SearchableSelect
                    value={reminderConfig.frequency}
                    onChange={(value) => setReminderConfig({ ...reminderConfig, frequency: value })}
                    options={[
                      { id: 'daily', label: 'Daily' },
                      { id: 'weekly', label: 'Weekly' }
                    ]}
                    getOptionLabel={(opt) => opt.label}
                    getOptionValue={(opt) => opt.id}
                    placeholder="Select frequency"
                    searchPlaceholder="Search..."
                    allowEmpty={false}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    How often to send reminder emails
                  </p>
                </div>

                {/* Day of Week (only for weekly) */}
                {reminderConfig.frequency === 'weekly' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Day of Week *
                    </label>
                    <SearchableSelect
                      value={reminderConfig.dayOfWeek}
                      onChange={(value) => setReminderConfig({ ...reminderConfig, dayOfWeek: parseInt(value) })}
                      options={[
                        { id: 0, label: 'Sunday' },
                        { id: 1, label: 'Monday' },
                        { id: 2, label: 'Tuesday' },
                        { id: 3, label: 'Wednesday' },
                        { id: 4, label: 'Thursday' },
                        { id: 5, label: 'Friday' },
                        { id: 6, label: 'Saturday' }
                      ]}
                      getOptionLabel={(opt) => opt.label}
                      getOptionValue={(opt) => opt.id}
                      placeholder="Select day"
                      searchPlaceholder="Search..."
                      allowEmpty={false}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Which day to send weekly reminders
                    </p>
                  </div>
                )}

                {/* Time of Day */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Time of Day *
                  </label>
                  <input
                    type="time"
                    value={reminderConfig.timeOfDay}
                    onChange={(e) => setReminderConfig({ ...reminderConfig, timeOfDay: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    What time to send reminders (24-hour format, server timezone)
                  </p>
                </div>

                {/* Summary */}
                <div className="bg-blue-50 border border-blue-200 rounded p-4">
                  <p className="text-sm text-blue-900">
                    <strong>Reminders will be sent:</strong>{' '}
                    {reminderConfig.frequency === 'daily'
                      ? `Daily at ${reminderConfig.timeOfDay}`
                      : `Every ${getDayName(reminderConfig.dayOfWeek)} at ${reminderConfig.timeOfDay}`
                    }
                  </p>
                </div>
              </>
            )}

            {/* Save Button */}
            <div className="pt-4">
              <button
                type="submit"
                disabled={savingReminder || !config.isConfigured}
                className="px-6 py-2 bg-blue-500 hover:bg-blue-600 text-white font-medium rounded-lg transition-colors duration-200 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {savingReminder ? 'Saving...' : 'Save Reminder Settings'}
              </button>
            </div>
          </div>
        </form>

        {/* Test Reminder Section */}
        {config.isConfigured && reminderConfig.enabled && (
          <div className="mt-8 pt-8 border-t border-gray-200">
            <h3 className="text-lg font-semibold mb-4">Test Reminder System</h3>
            <p className="text-sm text-gray-600 mb-4">
              Send test reminders immediately to all users with unmatched transactions
            </p>
            <button
              type="button"
              onClick={handleTestReminder}
              disabled={testingReminder}
              className="px-6 py-2 bg-green-500 hover:bg-green-600 text-white font-medium rounded-lg transition-colors duration-200 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {testingReminder ? 'Sending...' : 'Send Test Reminders Now'}
            </button>
          </div>
        )}
      </div>
      {/* Time Violation Notifications Section */}
      <div className="bg-white shadow-md rounded-lg p-6 mt-6">
        <div className="mb-4">
          <h2 className="text-xl font-bold flex items-center gap-2">
            <ShieldAlert className="h-5 w-5" />
            Time Violation Notifications
          </h2>
          <p className="text-gray-600 mt-2 text-sm">
            Configure email notifications sent to location managers when time clock violations are detected
          </p>
        </div>

        {!config.isConfigured && (
          <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-3 rounded mb-4 flex items-center gap-2">
            <AlertCircle className="h-5 w-5" />
            Email must be configured before enabling violation notifications
          </div>
        )}

        <form onSubmit={handleViolationNotifSubmit}>
          <div className="space-y-4">
            {/* Notify on New Violations Toggle */}
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="notifyOnNewViolations"
                checked={violationNotifConfig.notifyOnNewViolations}
                onChange={(e) => setViolationNotifConfig({ ...violationNotifConfig, notifyOnNewViolations: e.target.checked })}
                disabled={!config.isConfigured}
                className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
              />
              <label htmlFor="notifyOnNewViolations" className="text-sm font-medium text-gray-700">
                Notify managers when new violations are detected
              </label>
            </div>
            <p className="text-xs text-gray-500 ml-7 -mt-2">
              Sends an email to location managers immediately after a time clock file is processed and violations are found
            </p>

            {/* Weekly Reminder Info */}
            <div className="bg-amber-50 border border-amber-200 rounded p-4">
              <p className="text-sm text-amber-900 font-medium">Weekly Violation Reminder</p>
              <p className="text-xs text-amber-700 mt-1">
                A consolidated email is automatically sent every Saturday at 4:00 AM PT to managers with pending violations at their managed locations. Only violations from active (non-archived) uploads are included.
              </p>
            </div>

            {/* High Severity Only Toggle */}
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="notifyHighSeverityOnly"
                checked={violationNotifConfig.notifyHighSeverityOnly}
                onChange={(e) => setViolationNotifConfig({ ...violationNotifConfig, notifyHighSeverityOnly: e.target.checked })}
                disabled={!config.isConfigured}
                className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
              />
              <label htmlFor="notifyHighSeverityOnly" className="text-sm font-medium text-gray-700">
                Only notify for high severity violations
              </label>
            </div>
            <p className="text-xs text-gray-500 ml-7 -mt-2">
              When enabled, managers will only be notified about high severity violations (missing meal breaks for shifts over 6 hours). Warnings and medium severity will be excluded.
            </p>

            {/* Summary */}
            {violationNotifConfig.notifyOnNewViolations && (
              <div className="bg-blue-50 border border-blue-200 rounded p-4">
                <p className="text-sm text-blue-900">
                  <strong>Active notifications:</strong>{' '}
                  {[
                    violationNotifConfig.notifyOnNewViolations && 'On new violations',
                    'Weekly reminder (Saturday 4:00 AM PT)'
                  ].filter(Boolean).join(' • ')}
                  {violationNotifConfig.notifyHighSeverityOnly && ' (high severity only)'}
                </p>
              </div>
            )}

            {/* Save Button */}
            <div className="pt-4">
              <button
                type="submit"
                disabled={savingViolationNotif || !config.isConfigured}
                className="px-6 py-2 bg-blue-500 hover:bg-blue-600 text-white font-medium rounded-lg transition-colors duration-200 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {savingViolationNotif ? 'Saving...' : 'Save Notification Settings'}
              </button>
            </div>
          </div>
        </form>

        {/* Test Notification Section */}
        {config.isConfigured && (
          <div className="mt-8 pt-8 border-t border-gray-200">
            <h3 className="text-lg font-semibold mb-4">Test Violation Notifications</h3>
            <p className="text-sm text-gray-600 mb-4">
              Send a test weekly reminder email to all managers who have pending violations at their locations
            </p>
            <button
              type="button"
              onClick={handleTestViolationNotif}
              disabled={testingViolationNotif}
              className="px-6 py-2 bg-green-500 hover:bg-green-600 text-white font-medium rounded-lg transition-colors duration-200 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {testingViolationNotif ? 'Sending...' : 'Send Test Notification Now'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailConfiguration;
